import { Injectable } from '@angular/core';

interface HumanDetectResult {
    face?: Array<{ embedding?: number[] }>;
}

interface HumanInstance {
    load(): Promise<void>;
    warmup(): Promise<unknown>;
    detect(input: HTMLImageElement): Promise<HumanDetectResult>;
    match: {
        /** Similarite normalisee entre deux embeddings (>= 0.5 = meme personne). */
        similarity(a: number[], b: number[]): number;
    };
}

/**
 * Reconnaissance faciale basee sur @vladmandic/human (faceres).
 * Chargement differe : la lib et les modeles ne sont charges qu'a la
 * premiere capture, et uniquement si le POS active dS_FaceRecognition.
 */
@Injectable({ providedIn: 'root' })
export class FaceRecognitionService {

    private human?: HumanInstance;
    private initPromise?: Promise<boolean>;

    private async init(): Promise<boolean> {
        if (this.human) return true;
        this.initPromise ??= this.doInit();
        return this.initPromise;
    }

    private async doInit(): Promise<boolean> {
        try {
            const mod: any = await import('@vladmandic/human');
            const HumanClass = mod.default ?? mod.Human;
            this.human = new HumanClass({
                modelBasePath: new URL('assets/human-models/', document.baseURI).href,
                face: {
                    enabled: true,
                    detector: { enabled: true, modelPath: 'blazeface.json' },
                    mesh: { enabled: true, modelPath: 'facemesh.json' },
                    description: { enabled: true, modelPath: 'faceres.json' },
                    iris: { enabled: false },
                    age: { enabled: false },
                    gender: { enabled: false },
                    emotion: { enabled: false },
                    antispoof: { enabled: false },
                    liveness: { enabled: false }
                },
                body: { enabled: false },
                hand: { enabled: false },
                gesture: { enabled: false },
                object: { enabled: false },
                segmentation: { enabled: false }
            }) as HumanInstance;
            await this.human.load();
            await this.human.warmup();
            return true;
        } catch (err) {
            console.error('FaceRecognitionService init error:', err);
            this.initPromise = undefined;
            return false;
        }
    }

    /**
     * Similarite faceres entre deux embeddings (metrique officielle Human).
     * >= 0.5 = meme personne. Retourne 0 si la lib n'est pas chargee.
     */
    similarity(a: number[], b: number[]): number {
        if (!this.human || a.length !== b.length) return 0;
        return this.human.match.similarity(a, b);
    }

    /**
     * Extrait l'embedding facial d'une image (dataURL base64 ou Blob).
     * Retourne null si aucun visage n'est detecte ou si la lib n'est pas disponible.
     */
    async extractEmbedding(input: string | Blob): Promise<number[] | null> {
        if (!await this.init() || !this.human) return null;

        try {
            const image = await this.toImage(input);
            const result = await this.human.detect(image);
            const embedding = result.face?.[0]?.embedding;
            console.log(`[face-reco] visages detectes: ${result.face?.length ?? 0}, dims embedding: ${embedding?.length ?? 0}`);
            return embedding?.length ? Array.from(embedding) : null;
        } catch (err) {
            console.error('FaceRecognitionService detect error:', err);
            return null;
        }
    }

    private async toImage(input: string | Blob): Promise<HTMLImageElement> {
        const url = typeof input === 'string' ? input : URL.createObjectURL(input);
        try {
            const image = new Image();
            image.src = url;
            await image.decode();
            return image;
        } finally {
            if (typeof input !== 'string') URL.revokeObjectURL(url);
        }
    }
}
